import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Box, ClickAwayListener, Stack } from '@mui/material'
import { useLocation, useParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import TopBar from 'packages/ui/Arborist/components/Search/TopBar'
import TreeProvider, { useTreeContext } from 'packages/ui/Arborist/TreeProvider'
import { useSites, countUserSites, useUser, ACCOUNTS_LIMIT } from 'store'
import Popper from './Popper/Popper'
import SearchView from './SearchView'


function AccountsPerimeter() {
  const [user] = useUser()
  const {focusId} = useParams()
  const { accounts, selection, waiting, onlySite, resetSelection } = useTreeContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [_searchVerbatim, setSearchVerbatim] = useState('')
  const [searchVerbatim] = useDebounce(_searchVerbatim, 1000)
  const searchFieldRef = useRef(null)
  const searchInputRef = useRef(null)
  const mustReset = useMemo(() => {
    const isMonitoring = location.pathname.indexOf('/monitoring/ssids') === 0
      || location.pathname.indexOf('/monitoring/infras') === 0
    const sites = selection.filter(({is_site}) => is_site)
    return sites.length >= ACCOUNTS_LIMIT && isMonitoring && sites.length > 1
  }, [location.pathname, selection, user])

  const forceFirstSite = useMemo(() => {
    const sites = selection.filter(({is_site}) => is_site)
    if (sites.length === 1) {
      return false
    }
    if (countUserSites(user) === 1) {
      return true
    }
    return false
  }, [user, selection, focusId])
  useEffect(() => {
    if (mustReset) {
      resetSelection([])
    }
  }, [mustReset])
  useEffect(() => {
    if (forceFirstSite && !mustReset) {
      const site = [...accounts].sort((a,b) => a.name.localeCompare(b.name)).find(({ is_site }) => is_site)
      const newSelection = site.hierarchie
        .map(id => accounts.find(({ sf_id }) => sf_id === id))
        .filter(Boolean)
      resetSelection(newSelection)
    }
  }, [accounts, forceFirstSite, focusId])

  useEffect(() => {
    if (onlySite && !forceFirstSite && !mustReset) {
      const firstSite = selection.find(({ is_site }) => is_site)
      if (firstSite) {
        const newSelection = firstSite.hierarchie
          .map(id => selection.find(({ sf_id }) => sf_id === id))
          .filter(Boolean)
        resetSelection(newSelection)
      }
    }
  }, [onlySite, forceFirstSite])

  useEffect(() => {
    return () => {
      setAnchorEl(null)
      setSearchVerbatim('')
    }
  }, [])

  const handleClickAway = () => setAnchorEl(null)
  const handleInputClick = () => setAnchorEl(searchInputRef.current)

  return (
    <Box
      sx={{
        maxHeight: '36px',
        minHeight: '36px',
        width: {
          xs: '100%',
          sm: '100%',
          md: '500px'
        },
        cursor: waiting ? 'wait' : 'inherit'
      }}
      ref={searchInputRef}
      onClick={handleInputClick}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack width="inherit">
          <TopBar
            searchFieldRef={searchFieldRef}
            showCountChip={true}
            showUnfolded={false}
            onSearchChange={setSearchVerbatim}
          />
          <Popper open={Boolean(anchorEl)} anchorEl={anchorEl}>
            <SearchView
              searchVerbatim={searchVerbatim}
              accounts={accounts}
            />
          </Popper>
        </Stack>
      </ClickAwayListener>
    </Box>
  )
}

export default () => {
  const treeRef = useRef()
  const location = useLocation()
  const [user] = useUser()
  const [{ accounts, selected}, setSites] = useSites()

  const isMonitoring = useMemo(() => {
    return location.pathname.indexOf('/monitoring/ssids') === 0
      || location.pathname.indexOf('/monitoring/infras') === 0
  }, [location.pathname])

  const totalSites = useMemo(() => countUserSites(user), [user])

  const onlySite = useMemo(() => {
    if (location.pathname.indexOf('/voip') === 0) return true
    else if (isMonitoring) {
      return (selected.length ? selected.length : totalSites) >= ACCOUNTS_LIMIT
    }
    else if (location.pathname.indexOf('/ran') === 0) return true
    else if (location.pathname.indexOf('/iptv') === 0) return true
    else if (location.pathname.indexOf('/bypass') === 0) return true
    else if (location.pathname.indexOf('/stream-cast') === 0) return true
    else if (location.pathname.indexOf('/access-point-zones') === 0) return true
    else if (location.pathname.indexOf('/event-ssids') === 0) return true
    else if (location.pathname.indexOf('/monitoring/connected-users') === 0) return true
    else if (location.pathname.indexOf('/monitoring/topology') === 0) return true
    else if (location.pathname.indexOf('/monitoring-ssids') === 0) return true
    return false
  }, [location.pathname, totalSites])
  
  const disabledRemove = useMemo(() => {
    if (totalSites === 1) {
      return true
    }
    return isMonitoring && totalSites >= ACCOUNTS_LIMIT
  }, [location, totalSites, isMonitoring])

  useEffect(() => {
    setSites(prev => ({ ...prev, treeRef: treeRef?.current }))
  }, [])
  
  return (
    <TreeProvider
      hideCounters={true}
      onlySite={onlySite}
      canRemoveSelection={!disabledRemove}
      initialSelection={accounts}
      ref={treeRef}
      isMasterFilter={true}
    >
      <AccountsPerimeter />
    </TreeProvider>
  )
}
